import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { apiRequest, hasApiScope } from "../authConfig";
import { fetchCommesse } from "../api";
import "./CommessePage.css";

function CommessePage({ clienteId }) {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCommesse = async () => {
      if (!clienteId) {
        setCommesse([]);
        setLoading(false);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        if (!hasApiScope) {
          throw new Error("API scope non configurato. Imposta REACT_APP_API_SCOPE con lo scope dell'App Registration del backend.");
        }

        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
        } catch (silentError) {
          tokenResponse = await instance.acquireTokenPopup(apiRequest);
        }

        const data = await fetchCommesse(tokenResponse.accessToken, clienteId);
        setCommesse(Array.isArray(data) ? data : data?.commesse ?? []);
      } catch (err) {
        console.error("Errore nel caricamento delle commesse:", err);
        setError("Impossibile caricare le commesse. Verifica la connessione.");
      } finally {
        setLoading(false);
      }
    };

    if (accounts.length > 0) {
      loadCommesse();
    }
  }, [instance, accounts, clienteId]);

  const formatNumber = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? "-" : number.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const totaleOreRegistrate = commesse.reduce((sum, item) => sum + (parseFloat(item.oreRegistrate) || 0), 0);
  const totaleOreBudget = commesse.reduce((sum, item) => sum + (parseFloat(item.oreBudget) || 0), 0);

  const handleOpenRapportini = (commessa) => {
    navigate(`/rapportini?commessa=${encodeURIComponent(commessa)}`);
  };

  if (!clienteId) {
    return <div className="commesse-empty">Seleziona un cliente per visualizzare le commesse.</div>;
  }

  if (loading) {
    return <div className="commesse-loading">Caricamento commesse...</div>;
  }

  if (error) {
    return <div className="commesse-error">Errore nel caricamento delle commesse: {error}</div>;
  }

  if (!commesse || commesse.length === 0) {
    return <div className="commesse-empty">Nessuna commessa disponibile</div>;
  }

  return (
    <div className="commesse-page table-container">
      <div className="commesse-summary" style={{ display: "none" }}>
        <div>
          <span>Ore registrate totali</span>
          <strong>{formatNumber(totaleOreRegistrate)}</strong>
        </div>
        <div>
          <span>Ore budget totali</span>
          <strong>{formatNumber(totaleOreBudget)}</strong>
        </div>
        <div>
          <span>Ore residue totali</span>
          <strong>{formatNumber(totaleOreBudget - totaleOreRegistrate)}</strong>
        </div>
      </div>

      <table className="commesse-table">
        <thead>
          <tr>
            <th>Codice</th>
            <th>Tipologia</th>
            <th>Ore registrate</th>
            <th>Ore budget</th>
            <th>Ore residue</th>
            <th>Rapportini</th>
          </tr>
        </thead>
        <tbody>
          {commesse.map((item, index) => {
            const oreRegistrate = parseFloat(item.oreRegistrate) || 0;
            const oreBudget = parseFloat(item.oreBudget) || 0;
            const commessaCode = item.codice || item.id || `C-${index + 1}`;
            const isTimeAndMaterialConsuntivo = item.tipologia === "Time & Material - Consuntivo";
            return (
              <tr key={item.id ?? index}>
                <td>{commessaCode}</td>
                <td>{item.tipologia || "-"}</td>
                <td>{formatNumber(oreRegistrate)}</td>
                <td>{formatNumber(oreBudget)}</td>
                <td>{isTimeAndMaterialConsuntivo ? "N/A" : formatNumber(oreBudget - oreRegistrate)}</td>
                <td>
                  <button className="btn-commessa-view" onClick={() => handleOpenRapportini(commessaCode)}>
                    Apri rapportini
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CommessePage;
